import { useState, useEffect, lazy, Suspense } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  Phone,
  Mail,
  X,
  Send,
  Download,
  Upload,
} from "lucide-react";
import * as XLSX from "xlsx";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Card, CardContent } from "../components/ui/card";

const ITEMS_PER_PAGE = 20;

const emptyForm = () => ({
  destinatarioId: "" as string | number,
  nome: "",
  cargo: "",
  telefone: "",
  whatsapp: "",
  celular: "",
  telefonefixo: "",
  email: "",
  observacoes: "",
});

export default function Contacts() {
  const { user, loading: authLoading } = useGoogleAuth();
  const isCliente = user?.role === "cliente";
  const isAdminOrLider = user?.role === "admin" || user?.role === "lider";

  // Listagem
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterEmitenteId, setFilterEmitenteId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Modal criar/editar
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm());

  // Modal seleção destinatário
  const [isDestinatarioModalOpen, setIsDestinatarioModalOpen] = useState(false);
  const [destinatarioSearch, setDestinatarioSearch] = useState("");
  const [destinatarioPage, setDestinatarioPage] = useState(1);
  const DEST_PER_PAGE = 10;

  // Modal anotações
  const [anotacoesModalContatoId, setAnotacoesModalContatoId] = useState<number | null>(null);
  const [anotacoesModalContatoNome, setAnotacoesModalContatoNome] = useState("");
  const [novaAnotacao, setNovaAnotacao] = useState("");
  const [anotacoesPagina, setAnotacoesPagina] = useState(1);
  const ANOTACOES_POR_PAGINA = 20;

  // Auth redirect
  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = "/login";
    }
  }, [user, authLoading]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Queries
  const { data: emitentes = [] } = trpc.emitentes.list.useQuery(undefined, {
    enabled: !!user && !isCliente,
  });

  const { data: listData, refetch, isLoading } = trpc.contatos.list.useQuery({
    emitenteId: isCliente ? (user?.emitenteId ?? undefined) : (filterEmitenteId ?? undefined),
    search: debouncedSearch || undefined,
    page: currentPage,
    pageSize: ITEMS_PER_PAGE,
  });

  const contatos = listData?.data ?? [];
  const totalPages = listData?.totalPages ?? 1;
  const totalCount = listData?.total ?? 0;

  const { data: destinatariosData } = trpc.destinatarios.list.useQuery({
    emitenteId: isCliente ? (user?.emitenteId ?? undefined) : (filterEmitenteId ?? undefined),
  });
  const destinatarios = (destinatariosData as any)?.data ?? [];

  // Anotações
  const { data: anotacoes = [], refetch: refetchAnotacoes } = trpc.contatos.anotacoes.list.useQuery(
    { contatoId: anotacoesModalContatoId! },
    { enabled: anotacoesModalContatoId !== null }
  );

  // Mutations
  const createMutation = trpc.contatos.create.useMutation({
    onSuccess: () => {
      toast.success("Contato criado com sucesso!");
      refetch();
      setIsModalOpen(false);
      setForm(emptyForm());
    },
    onError: (e) => toast.error(e.message || "Erro ao criar contato"),
  });

  const updateMutation = trpc.contatos.update.useMutation({
    onSuccess: () => {
      toast.success("Contato atualizado com sucesso!");
      refetch();
      setIsModalOpen(false);
      setEditingId(null);
      setForm(emptyForm());
    },
    onError: (e) => toast.error(e.message || "Erro ao atualizar contato"),
  });

  const deleteMutation = trpc.contatos.delete.useMutation({
    onSuccess: () => {
      toast.success("Contato excluído com sucesso!");
      refetch();
    },
    onError: (e) => toast.error(e.message || "Erro ao excluir contato"),
  });

  const addAnotacaoMutation = trpc.contatos.anotacoes.add.useMutation({
    onSuccess: () => {
      setNovaAnotacao("");
      refetchAnotacoes();
    },
    onError: (e) => toast.error(e.message || "Erro ao adicionar anotação"),
  });

  const deleteAnotacaoMutation = trpc.contatos.anotacoes.delete.useMutation({
    onSuccess: () => refetchAnotacoes(),
    onError: (e) => toast.error(e.message || "Erro ao excluir anotação"),
  });

  const handleOpenCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setIsModalOpen(true);
  };

  const handleOpenEdit = (contato: any) => {
    setEditingId(contato.id);
    setForm({
      destinatarioId: contato.destinatarioId ?? "",
      nome: contato.nome ?? "",
      cargo: contato.cargo ?? "",
      telefone: contato.telefone ?? "",
      whatsapp: contato.whatsapp ?? "",
      celular: contato.celular ?? "",
      telefonefixo: contato.telefonefixo ?? "",
      email: contato.email ?? "",
      observacoes: contato.observacoes ?? "",
    });
    setIsModalOpen(true);
  };

  const handleSave = () => {
    if (!form.nome.trim()) {
      toast.error("Nome é obrigatório");
      return;
    }
    if (!form.destinatarioId) {
      toast.error("Cliente/Destinatário é obrigatório");
      return;
    }
    const payload = {
      destinatarioId: Number(form.destinatarioId),
      nome: form.nome.trim(),
      cargo: form.cargo || undefined,
      telefone: form.telefone || undefined,
      whatsapp: form.whatsapp || undefined,
      celular: form.celular || undefined,
      telefonefixo: form.telefonefixo || undefined,
      email: form.email || undefined,
      observacoes: form.observacoes || undefined,
    };
    if (editingId) {
      updateMutation.mutate({ id: editingId, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleDelete = (id: number, nome: string) => {
    if (confirm(`Excluir o contato "${nome}"?`)) {
      deleteMutation.mutate({ id });
    }
  };

  const handleOpenAnotacoes = (contato: any) => {
    setAnotacoesModalContatoId(contato.id);
    setAnotacoesModalContatoNome(contato.nome);
    setNovaAnotacao("");
    setAnotacoesPagina(1);
  };

  const handleAddAnotacao = () => {
    if (!novaAnotacao.trim() || !anotacoesModalContatoId) return;
    addAnotacaoMutation.mutate({ contatoId: anotacoesModalContatoId, texto: novaAnotacao.trim() });
  };

  const formatDate = (dt: any) => {
    if (!dt) return "";
    const d = new Date(dt);
    return d.toLocaleDateString("pt-BR") + " " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  };

  const handleExportExcel = () => {
    try {
      const dataToExport = contatos.map((c: any) => ({
        "ID": c.id,
        "DESTINATÁRIO ID": c.destinatarioId,
        "DESTINATÁRIO": c.destinatarioNome || "",
        "NOME": c.nome || "",
        "CARGO": c.cargo || "",
        "TELEFONE": c.telefone || "",
        "WHATSAPP": c.whatsapp || "",
        "CELULAR": c.celular || "",
        "TELEFONE FIXO": c.telefonefixo || "",
        "EMAIL": c.email || "",
        "OBSERVAÇÕES": c.observacoes || "",
      }));

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Contatos");
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `contatos_${new Date().toISOString().split("T")[0]}.xlsx`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Contatos exportados com sucesso!");
    } catch (error) {
      toast.error("Erro ao exportar contatos");
    }
  };

  const handleImportExcel = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      const items = jsonData.map((row: any) => ({
        destinatarioId: parseInt(row["DESTINATÁRIO ID"] || row["destinatarioId"]) || 0,
        nome: String(row["NOME"] || row["nome"] || ""),
        cargo: row["CARGO"] || row["cargo"] || undefined,
        telefone: row["TELEFONE"] || row["telefone"] || undefined,
        whatsapp: row["WHATSAPP"] || row["whatsapp"] || undefined,
        celular: row["CELULAR"] || row["celular"] || undefined,
        telefonefixo: row["TELEFONE FIXO"] || row["telefonefixo"] || undefined,
        email: row["EMAIL"] || row["email"] || undefined,
        observacoes: row["OBSERVAÇÕES"] || row["observacoes"] || undefined,
      })).filter((c: any) => c.nome && c.destinatarioId > 0);

      if (items.length === 0) {
        toast.error("Nenhum contato válido encontrado. Verifique se DESTINATÁRIO ID e NOME estão preenchidos.");
        return;
      }

      await trpc.contatos.createBulk.mutate({ items });
      toast.success(`${items.length} contatos importados com sucesso!`);
      refetch();
    } catch (error: any) {
      toast.error(error.message || "Erro ao importar contatos");
    }
    e.target.value = "";
  };

  const handleDownloadTemplate = () => {
    try {
      const templateData = [
        {
          "DESTINATÁRIO ID": 1,
          "NOME": "João Silva",
          "CARGO": "Gerente Comercial",
          "TELEFONE": "11 3333-4444",
          "WHATSAPP": "11 99999-8888",
          "CELULAR": "11 98888-7777",
          "TELEFONE FIXO": "11 3333-4444",
          "EMAIL": "joao@empresa.com",
          "OBSERVAÇÕES": "Contato principal",
        },
      ];

      const ws = XLSX.utils.json_to_sheet(templateData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Template");
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "template_contatos.xlsx");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Template baixado!");
    } catch (error) {
      toast.error("Erro ao baixar template");
    }
  };

  if (authLoading || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Contatos</h1>
          <p className="text-sm text-muted-foreground">{totalCount} contato(s) encontrado(s)</p>
        </div>
        <Button onClick={handleOpenCreate} className="bg-orange-500 hover:bg-orange-600">
          <Plus className="w-4 h-4 mr-2" />
          Novo Contato
        </Button>
        <Button variant="outline" onClick={handleExportExcel}>
          <Download className="w-4 h-4 mr-2" />
          Exportar Excel
        </Button>
        <Button variant="outline" onClick={handleDownloadTemplate}>
          <Download className="w-4 h-4 mr-2" />
          Baixar Template
        </Button>
        <label htmlFor="import-contatos">
          <Button variant="outline" asChild>
            <span>
              <Upload className="w-4 h-4 mr-2" />
              Importar Excel
            </span>
          </Button>
        </label>
        <input
          id="import-contatos"
          type="file"
          accept=".xlsx,.xls"
          className="hidden"
          onChange={handleImportExcel}
        />
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome, cargo, email ou telefone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        {!isCliente && (
          <Select
            value={filterEmitenteId ? String(filterEmitenteId) : "todos"}
            onValueChange={(v) => {
              setFilterEmitenteId(v === "todos" ? null : Number(v));
              setCurrentPage(1);
            }}
          >
            <SelectTrigger className="w-52">
              <SelectValue placeholder="Emitente" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os emitentes</SelectItem>
              {emitentes.map((em: any) => (
                <SelectItem key={em.id} value={String(em.id)}>
                  {em.razaoSocial}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Função</TableHead>
                  <TableHead>Cliente / Destinatário</TableHead>
                  {!isCliente && <TableHead>Emitente</TableHead>}
                  <TableHead>WhatsApp</TableHead>
                  <TableHead>Celular</TableHead>
                  <TableHead>Telefone Fixo</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="w-24 text-center">Anotações</TableHead>
                  <TableHead className="w-24 text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contatos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isCliente ? 9 : 10} className="text-center py-8 text-muted-foreground">
                      Nenhum contato encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  contatos.map((c: any) => (
                    <TableRow key={c.id}>
                      <TableCell className="font-medium">{c.nome}</TableCell>
                      <TableCell>{c.cargo || "-"}</TableCell>
                      <TableCell>{c.destinatarioNome || "-"}</TableCell>
                      {!isCliente && <TableCell>{c.emitenteNome || "-"}</TableCell>}
                      <TableCell>
                        {c.whatsapp ? (
                          <a
                            href={`https://wa.me/55${c.whatsapp.replace(/\D/g, "")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-green-600 hover:underline flex items-center gap-1"
                          >
                            <Phone className="w-3 h-3" />
                            {c.whatsapp}
                          </a>
                        ) : "-"}
                      </TableCell>
                      <TableCell>{c.celular || "-"}</TableCell>
                      <TableCell>{c.telefonefixo || "-"}</TableCell>
                      <TableCell>
                        {c.email ? (
                          <a href={`mailto:${c.email}`} className="text-blue-600 hover:underline flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {c.email}
                          </a>
                        ) : "-"}
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenAnotacoes(c)}
                          title="Ver / adicionar anotações"
                        >
                          <MessageSquare className="w-4 h-4 text-blue-500" />
                        </Button>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(c)} title="Editar">
                            <Edit className="w-4 h-4 text-orange-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(c.id, c.nome)}
                            title="Excluir"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Página {currentPage} de {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Modal Criar / Editar */}
      <Dialog open={isModalOpen} onOpenChange={(o) => { if (!o) { setIsModalOpen(false); setEditingId(null); setForm(emptyForm()); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar Contato" : "Novo Contato"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Cliente / Destinatário */}
            <div>
              <label className="text-sm font-medium mb-1 block">Cliente / Destinatário *</label>
              <div className="flex gap-2">
                <Input
                  value={form.destinatarioId ? ((destinatarios as any[]).find((d: any) => String(d.id) === String(form.destinatarioId))?.razaoSocial || "") : ""}
                  readOnly
                  placeholder="Selecione um destinatário"
                />
                <Button
                  type="button"
                  onClick={() => { setDestinatarioSearch(""); setDestinatarioPage(1); setIsDestinatarioModalOpen(true); }}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  Selecionar
                </Button>
              </div>
            </div>

            {/* Nome e Função */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Nome *</label>
                <Input
                  placeholder="Nome completo"
                  value={form.nome}
                  onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Função / Cargo</label>
                <Input
                  placeholder="Ex: Gerente Comercial"
                  value={form.cargo}
                  onChange={(e) => setForm((f) => ({ ...f, cargo: e.target.value }))}
                />
              </div>
            </div>

            {/* Telefones */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">WhatsApp</label>
                <Input
                  placeholder="(11) 99999-9999"
                  value={form.whatsapp}
                  onChange={(e) => setForm((f) => ({ ...f, whatsapp: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Celular</label>
                <Input
                  placeholder="(11) 99999-9999"
                  value={form.celular}
                  onChange={(e) => setForm((f) => ({ ...f, celular: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Telefone Fixo</label>
                <Input
                  placeholder="(11) 3333-3333"
                  value={form.telefonefixo}
                  onChange={(e) => setForm((f) => ({ ...f, telefonefixo: e.target.value }))}
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="text-sm font-medium mb-1 block">Email</label>
              <Input
                type="email"
                placeholder="email@empresa.com"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              />
            </div>

            {/* Observações */}
            <div>
              <label className="text-sm font-medium mb-1 block">Observações</label>
              <Textarea
                placeholder="Observações sobre este contato..."
                value={form.observacoes}
                onChange={(e) => setForm((f) => ({ ...f, observacoes: e.target.value }))}
                rows={3}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => { setIsModalOpen(false); setEditingId(null); setForm(emptyForm()); }}>
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={createMutation.isPending || updateMutation.isPending}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {createMutation.isPending || updateMutation.isPending ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Anotações */}
      <Dialog
        open={anotacoesModalContatoId !== null}
        onOpenChange={(o) => { if (!o) { setAnotacoesModalContatoId(null); setNovaAnotacao(""); } }}
      >
        <DialogContent className="max-w-xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Anotações — {anotacoesModalContatoNome}</DialogTitle>
          </DialogHeader>

          {/* Lista de anotações */}
          <div className="flex-1 overflow-y-auto space-y-3 py-2 pr-1">
            {anotacoes.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">Nenhuma anotação ainda.</p>
            ) : (
              (anotacoes as any[])
                .slice((anotacoesPagina - 1) * ANOTACOES_POR_PAGINA, anotacoesPagina * ANOTACOES_POR_PAGINA)
                .map((a: any) => (
                  <div key={a.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <p className="text-sm whitespace-pre-wrap">{a.texto}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          <span className="font-medium">{a.autorNome}</span> · {formatDate(a.criadoEm)}
                        </p>
                      </div>
                      {(isAdminOrLider || a.autorEmail === user?.email) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                          onClick={() => deleteAnotacaoMutation.mutate({ id: a.id })}
                          title="Excluir anotação"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))
            )}
          </div>

          {/* Paginação de anotações */}
          {(anotacoes as any[]).length > ANOTACOES_POR_PAGINA && (
            <div className="flex items-center justify-between border-t pt-2 flex-shrink-0">
              <span className="text-xs text-muted-foreground">
                Página {anotacoesPagina} de {Math.ceil((anotacoes as any[]).length / ANOTACOES_POR_PAGINA)} · {(anotacoes as any[]).length} anotações
              </span>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={anotacoesPagina <= 1}
                  onClick={() => setAnotacoesPagina((p) => p - 1)}
                >
                  <ChevronLeft className="w-3 h-3" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={anotacoesPagina >= Math.ceil((anotacoes as any[]).length / ANOTACOES_POR_PAGINA)}
                  onClick={() => setAnotacoesPagina((p) => p + 1)}
                >
                  <ChevronRight className="w-3 h-3" />
                </Button>
              </div>
            </div>
          )}

          {/* Campo para nova anotação */}
          <div className="border-t pt-3 flex gap-2 flex-shrink-0">
            <Textarea
              placeholder="Escreva uma anotação..."
              value={novaAnotacao}
              onChange={(e) => setNovaAnotacao(e.target.value)}
              rows={2}
              className="flex-1 resize-none"
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  handleAddAnotacao();
                }
              }}
            />
            <Button
              onClick={handleAddAnotacao}
              disabled={!novaAnotacao.trim() || addAnotacaoMutation.isPending}
              className="self-end bg-orange-500 hover:bg-orange-600"
              title="Enviar (Ctrl+Enter)"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">Ctrl+Enter para enviar</p>
        </DialogContent>
      </Dialog>

      {/* Modal Seleção de Destinatário */}
      <Dialog open={isDestinatarioModalOpen} onOpenChange={setIsDestinatarioModalOpen}>
        <DialogContent className="max-w-5xl sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>Selecionar Destinatário</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder="Buscar por razão social, fantasia, CNPJ ou CPF..."
              value={destinatarioSearch}
              onChange={(e) => {
                setDestinatarioSearch(e.target.value);
                setDestinatarioPage(1);
              }}
            />
            <div className="border rounded-lg">
              <div className="max-h-[60vh] overflow-auto">
                <table className="w-full border-collapse" style={{minWidth: '600px'}}>
                  <thead className="sticky top-0 bg-muted">
                    <tr>
                      <th className="border-b p-3 text-left" style={{minWidth: '200px'}}>Razão Social</th>
                      <th className="border-b p-3 text-left" style={{minWidth: '150px'}}>Fantasia</th>
                      <th className="border-b p-3 text-left" style={{minWidth: '140px'}}>CNPJ/CPF</th>
                      <th className="border-b p-3 text-center" style={{minWidth: '110px'}}>Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const filtered = (destinatarios as any[]).filter((d: any) =>
                        destinatarioSearch === "" ||
                        d.razaoSocial?.toLowerCase().includes(destinatarioSearch.toLowerCase()) ||
                        d.nomeFantasia?.toLowerCase().includes(destinatarioSearch.toLowerCase()) ||
                        d.cnpj?.toLowerCase().includes(destinatarioSearch.toLowerCase()) ||
                        d.cpf?.toLowerCase().includes(destinatarioSearch.toLowerCase())
                      );
                      const totalPages = Math.ceil(filtered.length / DEST_PER_PAGE);
                      const startIndex = (destinatarioPage - 1) * DEST_PER_PAGE;
                      const endIndex = startIndex + DEST_PER_PAGE;
                      const paginatedData = filtered.slice(startIndex, endIndex);

                      if (paginatedData.length === 0) {
                        return (
                          <tr>
                            <td colSpan={4} className="p-4 text-center text-muted-foreground">Nenhum destinatário encontrado.</td>
                          </tr>
                        );
                      }

                      return paginatedData.map((d: any) => (
                        <tr key={d.id} className="hover:bg-muted/50">
                          <td className="border-b p-3">{d.razaoSocial || '-'}</td>
                          <td className="border-b p-3">{d.nomeFantasia || '-'}</td>
                          <td className="border-b p-3 whitespace-nowrap">{d.cnpj || d.cpf || '-'}</td>
                          <td className="border-b p-3 text-center">
                            <Button
                              size="sm"
                              onClick={() => {
                                setForm((f) => ({ ...f, destinatarioId: String(d.id) }));
                                setIsDestinatarioModalOpen(false);
                                setDestinatarioSearch("");
                              }}
                              className="bg-orange-500 hover:bg-orange-600"
                            >
                              Selecionar
                            </Button>
                          </td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>

              {/* Paginação */}
              {(() => {
                const filtered = (destinatarios as any[]).filter((d: any) =>
                  destinatarioSearch === "" ||
                  d.razaoSocial?.toLowerCase().includes(destinatarioSearch.toLowerCase()) ||
                  d.nomeFantasia?.toLowerCase().includes(destinatarioSearch.toLowerCase()) ||
                  d.cnpj?.toLowerCase().includes(destinatarioSearch.toLowerCase()) ||
                  d.cpf?.toLowerCase().includes(destinatarioSearch.toLowerCase())
                );
                const totalPages = Math.ceil(filtered.length / DEST_PER_PAGE);

                if (totalPages <= 1) return null;

                return (
                  <div className="flex items-center justify-between p-3 border-t bg-muted/30">
                    <div className="text-sm text-muted-foreground">
                      Página {destinatarioPage} de {totalPages} ({filtered.length} destinatários)
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDestinatarioPage(p => Math.max(1, p - 1))}
                        disabled={destinatarioPage === 1}
                      >
                        Anterior
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDestinatarioPage(p => Math.min(totalPages, p + 1))}
                        disabled={destinatarioPage === totalPages}
                      >
                        Próxima
                      </Button>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
